<template>
  <div
    :class="{ large: isLarge }"
    :style="{
      opacity: selectedTrestle ? (selectedTrestle === trestle ? 1 : 0.3) : 1,
    }"
    class="single-trestle-container"
  >
    <div
      class="pa-1 flex-1 d-flex flex-column"
      style="border-left: dashed 1px #6b6b6b; border-right: dashed 1px #6b6b6b"
    >
      <div
        v-for="(position, positionIndex) in trestle.positions"
        :key="positionIndex"
        class="d-flex flex-1 overflow-hidden"
      >
        <div
          v-if="position.elements.length === 0"
          class="flex-1 mb-1 d-flex align-center justify-center px-1"
          style="
            border: dotted 1px #6b6b6b;
            background: transparent;
            height: 25px;
          "
        >
          <span style="font-size: 9px; color: #9e9e9e">{{ position.length }} mm</span>
        </div>
        <v-tooltip
          v-for="(element, elementIndex) in position.elements"
          v-else
          :key="elementIndex"
          bottom
        >
          <template #activator="{ on }">
            <div
              :class="{
                'mx-1': elementIndex < position.elements.length,
              }"
              :style="{
                border: `2px solid ${moduleColor(element.module_id)}`,
                background: typeColorDictionary[element?.module_type],
                opacity:
                  selectedModule !== null
                    ? selectedModule === element?.module_id
                      ? 1
                      : 0.3
                    : 1,
              }"
              class="flex-1 mb-1 d-flex align-center justify-center c-pointer element-cell"
              @click="$emit('clickedElement', element)"
              v-on="on"
            >
              <span class="element-pos-label">{{ positionIndex + 1 }}.{{ elementIndex + 1 }}</span>
              <v-icon v-if="element?.production_id" class="element-check-icon" x-small
                >mdi-check-circle
              </v-icon>
              <div class="element-type-label">
                {{ element.element_type ? element.element_type : '-' }}
              </div>
            </div>
          </template>
          <span>
            {{ element.element_id }}<br />
            {{ element.width }}×{{ element.height }} mm | {{ element.length }} mm<br />
            Remaining: {{ position.length }} mm ({{ position.elements.length }}
            el)
          </span>
        </v-tooltip>
      </div>

      <div class="d-flex align-center mt-auto pt-1 width-usage-row">
        <span class="width-usage-label">{{ correctedWidthUsed }}/560</span>
        <div class="flex-1 width-usage-track ml-1">
          <div
            class="width-usage-fill"
            :style="{
              width: `${Math.min((correctedWidthUsed / 560) * 100, 100)}%`,
              background: widthUsageColor(correctedWidthUsed),
            }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Sweep the trestle length: sum widths of all active elements at each X interval.
// Elements within a slot are packed end-to-end, so different elements in the same
// slot occupy different X ranges and may have different widths.
function maxTrestleWidth(positions) {
  const segments = [];
  for (const pos of positions) {
    let x = 0;
    for (const el of pos.elements) {
      const len = el.length || 4800;
      segments.push({ x0: x, x1: x + len, w: el.width || 0 });
      x += len;
    }
  }
  if (!segments.length) return 0;
  const xs = [...new Set(segments.flatMap((s) => [s.x0, s.x1]))].sort((a, b) => a - b);
  let max = 0;
  for (let i = 0; i < xs.length - 1; i++) {
    const mid = (xs[i] + xs[i + 1]) / 2;
    const total = segments.filter((s) => s.x0 <= mid && mid < s.x1).reduce((sum, s) => sum + s.w, 0);
    if (total > max) max = total;
  }
  return max;
}

export default {
  name: 'BaseDhmeSingleTrestle',
  props: {
    trestle: {
      type: Object,
      required: true,
    },
    selectedTrestle: {
      type: Object,
      required: false,
      default: null,
    },
    typeColorDictionary: {
      type: Object,
      required: true,
    },
    selectedModule: {
      type: String,
      required: false,
      default: null,
    },
    isLarge: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    correctedWidthUsed() {
      return maxTrestleWidth(this.trestle.positions);
    },
  },
  methods: {
    moduleColor(moduleId) {
      if (!moduleId) return '#6b6b6b';
      let hash = 0;
      for (let i = 0; i < moduleId.length; i++) {
        hash = moduleId.charCodeAt(i) + ((hash << 5) - hash);
      }
      return `hsl(${Math.abs(hash) % 360}, 60%, 45%)`;
    },
    widthUsageColor(used) {
      const pct = used / 560;
      if (pct >= 1) return '#f44336';
      if (pct >= 0.9) return '#ff9800';
      return '#4caf50';
    },
  },
};
</script>

<style lang="scss" scoped>
.element-cell {
  position: relative;
  min-width: 0;
}

.element-pos-label {
  position: absolute;
  top: 2px;
  left: 2px;
  font-size: 7px;
  line-height: 1;
  opacity: 0.55;
  pointer-events: none;
}

.element-check-icon {
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 10px !important;
}

.element-type-label {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
  font-size: 9px;
  padding: 0 4px;
}

.single-trestle-container {
  width: 250px;
  height: 165px;
  border-bottom: solid 10px #6b6b6b;
  border-top: solid 2px #6b6b6b;
  display: flex;
  flex-direction: column;
  background-color: rgba(255, 255, 255, 0.75);
  position: relative;
  overflow: hidden;

  &.large {
    width: 500px;
    height: 350px;
  }
}

.width-usage-row {
  min-height: 10px;
}

.width-usage-label {
  font-size: 8px;
  color: #6b6b6b;
  flex-shrink: 0;
  white-space: nowrap;
}

.width-usage-track {
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
}

.width-usage-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s, background 0.3s;
}
</style>
